import { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Seo } from '../../../../lib/seo';
import { api } from '../../../../lib/api';
import { useAuth } from '../../../../lib/auth-context';
import { useTheme } from '../../../../lib/theme';
import { collections, type CollectionKey } from '../admin-collections.config';
import { Editor } from '../AdminCollectionEditor';
import { ErrorBoundary } from '../../../../components/ErrorBoundary';

import './Module.scss';




// Reexportações dos módulos de páginas
export { Editor as AdminCollectionEditorPage } from '../AdminCollectionEditor';
export { SettingsEditor as AdminSettingsPage } from '../AdminSettingsEditor';
export { ServerSettingsPage } from '../ServerSettingsPage';
export { MessagesEditor as AdminMessagesPage } from '../AdminMessagesEditor';
export { UsersEditor as AdminUsersPage } from '../AdminUsersEditor';
export { AdminOverviewPage } from '../AdminOverviewPage';
export { AdminErrorsPage } from '../AdminErrorsPage';

/* ==========================================================================
   SUBCOMPONENTE: PÁGINA INICIAL / DASHBOARD ANALÍTICO (BI & MÉTRICAS)
   ========================================================================== */

interface DashboardStats {
  services?: number;
  projects?: number;
  clients?: number;
  newMessages?: number;
  partners?: number;
}

/* ==========================================================================
   COMPONENTE PRINCIPAL: LAYOUT COM SIDEBAR, HEADER E FOOTER
   ========================================================================== */

export function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [cmsMenuOpen, setCmsMenuOpen] = useState<boolean>(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const userInitial = user?.email ? user.email.charAt(0).toUpperCase() : 'A';

  return (
    <>
      <Seo title="Painel Administrativo" description="Painel de controle A3GS Tec." path="/admin" noIndex />

      <div className={`dash-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>

        {/* SIDEBAR RETRÁTIL */}
        <aside className="dash-sidebar">
          <div className="dash-sidebar-header">
            <Link to="/admin" className="dash-brand-link">
              <div className="dash-brand-logo">A3</div>
              {!sidebarCollapsed && <span className="dash-brand-title">A3GS <strong>Tec</strong></span>}
            </Link>

          </div>

          <nav className="dash-nav">
            <div className="dash-nav-group">
              {!sidebarCollapsed && <span className="dash-nav-label">PRINCIPAL</span>}
              <NavLink
                to="/admin"
                end
                className={({ isActive }) => `dash-nav-item ${isActive ? 'active' : ''}`}
                title="Visão Geral"
              >
                <Icon icon="lucide:layout-dashboard" width="20" height="20" className="dash-nav-icon" />
                {!sidebarCollapsed && <span> Visão Geral </span>}
              </NavLink>
            </div>

            {/* Módulos CMS com Submenu */}
            <div className="dash-nav-group">
              {!sidebarCollapsed && <span className="dash-nav-label">CONTEÚDO</span>}
              <div
                className={`dash-nav-item dash-nav-parent ${location.pathname.startsWith('/admin/') &&
                  !['/admin/settings', '/admin/server', '/admin/messages', '/admin/users'].includes(location.pathname)
                  ? 'active-parent'
                  : ''
                  }`}
                onClick={() => {
                  if (sidebarCollapsed) setSidebarCollapsed(false);
                  setCmsMenuOpen(!cmsMenuOpen);
                }}
                title="Módulos CMS"
              >
                <Icon icon="lucide:folder-kanban" width="20" height="20" className="dash-nav-icon" />
                {!sidebarCollapsed && (
                  <>
                    <span style={{ flex: 1 }}>Módulos CMS</span>
                    <Icon
                      icon={cmsMenuOpen ? 'lucide:chevron-down' : 'lucide:chevron-right'}
                      width="16"
                      height="16"
                      className="dash-chevron"
                    />
                  </>
                )}
              </div>

              {(cmsMenuOpen || sidebarCollapsed) && (
                <div className={`dash-submenu ${sidebarCollapsed ? 'dash-submenu-floating' : ''}`}>
                  {Object.entries(collections).map(([key, meta]) => (
                    <NavLink
                      key={key}
                      to={`/admin/${key}`}
                      className={({ isActive }) => `dash-sub-item ${isActive ? 'active' : ''}`}
                      title={meta.title}
                    >
                      <Icon icon="lucide:file-text" width="16" height="16" />
                      {!sidebarCollapsed && <span>{meta.title}</span>}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            {/* Gestão e Configurações */}
            <div className="dash-nav-group">
              {!sidebarCollapsed && <span className="dash-nav-label">SISTEMA</span>}
              <NavLink
                to="/admin/messages"
                className={({ isActive }) => `dash-nav-item ${isActive ? 'active' : ''}`}
                title="Mensagens"
              >
                <Icon icon="lucide:inbox" width="20" height="20" className="dash-nav-icon" />
                {!sidebarCollapsed && <span>Mensagens</span>}
              </NavLink>
              <NavLink
                to="/admin/users"
                className={({ isActive }) => `dash-nav-item ${isActive ? 'active' : ''}`}
                title="Usuários"
              >
                <Icon icon="lucide:shield-check" width="20" height="20" className="dash-nav-icon" />
                {!sidebarCollapsed && <span>Usuários & Acessos</span>}
              </NavLink>
              <NavLink
                to="/admin/settings"
                className={({ isActive }) => `dash-nav-item ${isActive ? 'active' : ''}`}
                title="Configurações SEO"
              >
                <Icon icon="lucide:sliders-horizontal" width="20" height="20" className="dash-nav-icon" />
                {!sidebarCollapsed && <span>Configurações & SEO</span>}
              </NavLink>
              <NavLink
                to="/admin/server"
                className={({ isActive }) => `dash-nav-item ${isActive ? 'active' : ''}`}
                title="Servidor e banco de dados"
              >
                <Icon icon="lucide:server-cog" width="20" height="20" className="dash-nav-icon" />
                {!sidebarCollapsed && <span>Servidor e Banco</span>}
              </NavLink>
            </div>
          </nav>
        </aside>

        {/* ÁREA PRINCIPAL */}
        <div className="dash-main-wrapper">

          {/* HEADER */}
          <header className="dash-header">

            <div className="dash-header-left">

              <button
                type="button"
                className="dash-collapse-btn"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                title={sidebarCollapsed ? 'Expandir Menu' : 'Recolher Menu'}
              >
                <Icon
                  icon={sidebarCollapsed ? 'lucide:panel-left-open' : 'lucide:panel-left-close'}
                  width="20"
                  height="20"
                />
              </button>

              <button
                type="button"
                className="dash-mobile-toggle"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              >
                <Icon icon="lucide:menu" width="22" height="22" />
              </button>
              <span className="dash-header-breadcrumb">
                Admin{location.pathname.replace('/admin', '') || 'Visão Geral'}
              </span>
            </div>

            <div className="dash-header-right">

              <Link to="/" target="_blank" className="dash-view-site-btn" title="Abrir site público">
                <Icon icon="lucide:external-link" width="16" height="16" />
                <span>Site Público</span>
              </Link>

              {/* DROPDOWN DO USUÁRIO */}
              <div className="dash-user-menu" ref={dropdownRef}>
                <button
                  type="button"
                  className="dash-avatar-btn"
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  aria-label="Menu de usuário"
                >
                  <div className="dash-avatar-circle">{userInitial}</div>
                </button>

                {userDropdownOpen && (
                  <div className="dash-user-dropdown">
                    <div className="dash-dropdown-header">
                      <div className="dash-avatar-large">{userInitial}</div>
                      <div className="dash-user-details">
                        <strong>{user?.email || 'Administrador'}</strong>
                        <span className="dash-user-role">{user?.role || 'Admin'}</span>
                      </div>
                    </div>

                    <div className="dash-dropdown-divider" />

                    <button
                      type="button"
                      className="dash-dropdown-item"
                      onClick={() => {
                        setTheme(theme === 'dark' ? 'light' : 'dark');
                      }}
                    >
                      <Icon icon={theme === 'dark' ? 'lucide:sun' : 'lucide:moon'} width="18" height="18" />
                      <span>Modo {theme === 'dark' ? 'Claro' : 'Escuro'}</span>
                    </button>

                    <button
                      type="button"
                      className="dash-dropdown-item"
                      onClick={() => {
                        setUserDropdownOpen(false);
                        navigate('/admin/users');
                      }}
                    >
                      <Icon icon="lucide:user-plus" width="18" height="18" />
                      <span>Criar Nova Conta</span>
                    </button>

                    <div className="dash-dropdown-divider" />

                    <button
                      type="button"
                      className="dash-dropdown-item dash-item-danger"
                      onClick={() => {
                        setUserDropdownOpen(false);
                        void logout();
                      }}
                    >
                      <Icon icon="lucide:log-out" width="18" height="18" />
                      <span>Desconectar / Sair</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* CONTEÚDO */}
          <main className="dash-content-body">
            <ErrorBoundary scope="Módulo administrativo"><Outlet /></ErrorBoundary>
          </main>

          {/* FOOTER */}
          <footer className="dash-footer">
            <div className="dash-footer-left">
              <span>© {new Date().getFullYear()} A3GS Tec — Gestão Integrada</span>
            </div>
            <div className="dash-footer-right">
              <span className="dash-footer-badge">Cloudflare Pages • D1/MySQL</span>
            </div>
          </footer>
        </div>

      </div>
    </>
  );
}

export function AdminCollectionRouteWrapper() {
  const { collection } = useParams<{ collection: CollectionKey }>();
  const validKey = (collection && collection in collections ? collection : 'services') as CollectionKey;
  return <Editor name={validKey} />;
}

export function AdminPlaceholderPage({ title }: { title: string }) {
  return (
    <div className="dash-card" style={{ maxWidth: '600px', margin: '40px auto', textAlign: 'center' }}>
      <h2>{title}</h2>
      <p className="muted">Este módulo agora é gerenciado diretamente no novo painel centralizado.</p>
      <Link className="btn btn-primary" to="/admin" style={{ marginTop: '16px' }}>
        Voltar à Visão Geral
      </Link>
    </div>
  );
}